import { CallStatusesEnum } from '@/types/CallStatusesEnum';
import { type ICallHistoryEvent } from '@/types/ICallHistoryEvent';
import { isCallEventData } from './isCallEventData';
import { isRecord } from './isRecord';

/** Маркер топика истории в поле `event` — аналог `order_post_persist` у заказов. */
const HISTORY_EVENT_KEY = 'account_calls_persist';

const CALL_STATUSES: ReadonlySet<string> = new Set(
  Object.values(CallStatusesEnum)
);

const isCallStatus = (value: unknown): value is CallStatusesEnum =>
  typeof value === 'string' && CALL_STATUSES.has(value);

/** Статус исторического события: известное значение либо ещё не проставленный `null`. */
const isHistoryStatus = (value: unknown): value is CallStatusesEnum | null =>
  value === null || isCallStatus(value);

/**
 * Type guard исторического события звонка из топика `admin_calls_history`.
 *
 * Принимает событие по любому из двух признаков: маркеру
 * `event === 'account_calls_persist'` либо `status` из `CallStatusesEnum`.
 * Маркер нужен ради случая, когда статус ещё не проставлен (`null`): без него
 * такое событие не опозналось бы вовсе и потерялось молча, а с ним оно хотя бы
 * пополнит реестр закрытых звонков и погасит зависшую карточку входящего.
 *
 * Незнакомую строку в `status` не пропускаем: тип объявляет
 * `CallStatusesEnum | null`, и guard обязан доказывать ровно это.
 * `datetime` допускаем отсутствующим — форма топика подтверждена не до конца.
 *
 * @param value - произвольное значение (распарсенный JSON)
 * @returns `true`, если значение соответствует форме `ICallHistoryEvent`
 */
export const isCallHistoryEvent = (
  value: unknown
): value is ICallHistoryEvent =>
  isRecord(value) &&
  (value.event === undefined || typeof value.event === 'string') &&
  (value.event === HISTORY_EVENT_KEY || isCallStatus(value.status)) &&
  isHistoryStatus(value.status) &&
  (value.datetime === undefined || typeof value.datetime === 'number') &&
  isCallEventData(value.data);
